/**
 * Optimized Store Switching Hook
 * Handles cache clearing and preloading when user switches stores
 * Makes store switching feel instant
 */

import { useEffect, useRef } from 'react';
import { amazonApi } from '../services/cachedApi';
import { resolveDateRangeBounds } from '../utils/dateRangeUtils';
import { scheduleDashboardPreload } from '../services/amazonPreload';
import { clearAllDashboardSessions } from '../utils/dashboardSessionCache';

function storeSwitchPreloadEnabled() {
  return process.env.REACT_APP_ENABLE_STORE_SWITCH_PRELOAD === '1';
}

export function useOptimizedStoreSwitch(selectedProfile) {
  const previousProfileRef = useRef(null);
  const warmedProfileRef = useRef(null);

  useEffect(() => {
    if (!selectedProfile) return;

    const currentProfileId = selectedProfile.profileId;
    const previousProfileId = previousProfileRef.current;

    if (previousProfileId && previousProfileId !== currentProfileId) {
      amazonApi.clearAllCaches();
      clearAllDashboardSessions();
      warmedProfileRef.current = null;
    }

    if (storeSwitchPreloadEnabled() && warmedProfileRef.current !== currentProfileId) {
      warmedProfileRef.current = currentProfileId;
      preloadStoreOptimizationSurfaces(selectedProfile);
    } else if (warmedProfileRef.current !== currentProfileId) {
      warmedProfileRef.current = currentProfileId;
    }

    previousProfileRef.current = currentProfileId;
  }, [selectedProfile]);
}

/**
 * Warm merged metrics + bulk/campaign caches when a store is selected (background).
 */
function preloadStoreOptimizationSurfaces(profile) {
  if (!profile?.profileId) return;

  const { startDate, endDate } = resolveDateRangeBounds('30d');
  const profileId = profile.profileId;

  scheduleDashboardPreload(
    profileId,
    { startDate, endDate, period: '30' },
    { includeBulk: false }
  );
}

export default useOptimizedStoreSwitch;

